<template>
  <transition name="slider-overlay">
    <div
      v-click-outside="handleClickOutside"
      class="slider-overlay"
      :style="positionStyle"
      @click.stop
    >
      <!-- Connection line to parent icon -->
      <div class="slider-connector" />

      <!-- Slider content -->
      <div class="slider-content">
        <!-- Header -->
        <div class="slider-header">
          <span class="slider-label">{{ label }}</span>
          <span class="slider-value">{{ displayValue >= max ? 'Max' : displayValue + unit }}</span>
        </div>

        <!-- Slider track -->
        <div class="slider-track-container">
          <div class="slider-track" ref="track" @click="handleTrackClick">
            <!-- Filled portion -->
            <div class="slider-fill" :style="{ width: fillPercent + '%' }" />

            <!-- Tick marks -->
            <div class="slider-ticks">
              <div
                v-for="tick in ticks"
                :key="tick"
                class="slider-tick"
                :style="{ left: tick + '%' }"
              />
            </div>

            <!-- Thumb -->
            <div
              class="slider-thumb"
              :style="{ left: fillPercent + '%' }"
              @mousedown="startDrag"
            >
              <div class="slider-thumb-inner" />
            </div>
          </div>
        </div>

        <!-- Min/Max labels -->
        <div class="slider-bounds">
          <span class="slider-min">{{ min }}{{ unit }}</span>
          <span class="slider-max">Max</span>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: 'SliderOverlay',

  props: {
    value: {
      type: Number,
      required: true,
    },
    label: {
      type: String,
      default: 'Value',
    },
    unit: {
      type: String,
      default: '',
    },
    min: {
      type: Number,
      default: 0,
    },
    max: {
      type: Number,
      default: 100,
    },
    logScale: {
      type: Boolean,
      default: false,
    },
    position: {
      type: Object,
      default: () => ({ x: 0, y: 0 }),
    },
  },

  data() {
    return {
      isDragging: false,
      ticks: [0, 25, 50, 75, 100],
    };
  },

  computed: {
    positionStyle() {
      return {
        left: `${this.position.x}px`,
        top: `${this.position.y}px`,
      };
    },

    // Convert actual value to slider position (0-100)
    fillPercent() {
      if (this.logScale) {
        return this.inverseLogScale(this.value);
      }
      return ((this.value - this.min) / (this.max - this.min)) * 100;
    },

    displayValue() {
      return Math.round(this.value);
    },
  },

  methods: {
    // Log scale conversion: slider position -> actual value
    logScaleValue(percent) {
      const minLog = Math.log(this.min || 1);
      const maxLog = Math.log(this.max);
      const scale = (maxLog - minLog) / 100;
      return Math.exp(minLog + scale * percent);
    },

    // Inverse log scale: actual value -> slider position
    inverseLogScale(value) {
      const minLog = Math.log(this.min || 1);
      const maxLog = Math.log(this.max);
      const scale = (maxLog - minLog) / 100;
      return (Math.log(value) - minLog) / scale;
    },

    // Convert percent to actual value
    percentToValue(percent) {
      const clampedPercent = Math.max(0, Math.min(100, percent));

      if (this.logScale) {
        return this.logScaleValue(clampedPercent);
      }

      return this.min + (clampedPercent / 100) * (this.max - this.min);
    },

    handleTrackClick(event) {
      const track = this.$refs.track;
      const rect = track.getBoundingClientRect();
      const percent = ((event.clientX - rect.left) / rect.width) * 100;
      const newValue = this.percentToValue(percent);
      this.$emit('input', newValue);
    },

    startDrag(event) {
      event.preventDefault();
      this.isDragging = true;

      document.addEventListener('mousemove', this.handleDrag);
      document.addEventListener('mouseup', this.stopDrag);
    },

    handleDrag(event) {
      if (!this.isDragging) return;

      const track = this.$refs.track;
      const rect = track.getBoundingClientRect();
      const percent = ((event.clientX - rect.left) / rect.width) * 100;
      const newValue = this.percentToValue(percent);
      this.$emit('input', newValue);
    },

    stopDrag() {
      this.isDragging = false;
      document.removeEventListener('mousemove', this.handleDrag);
      document.removeEventListener('mouseup', this.stopDrag);
    },

    handleClickOutside() {
      this.$emit('close');
    },
  },

  beforeDestroy() {
    document.removeEventListener('mousemove', this.handleDrag);
    document.removeEventListener('mouseup', this.stopDrag);
  },
};
</script>

<style lang="scss" scoped>
// ============================================================================
// Slider Overlay - Precision Control Interface
// With Light/Dark Theme Support
// ============================================================================

.slider-overlay {
  position: absolute;
  transform: translateX(-50%);
  z-index: 1001;
  border-radius: var(--ds-radius-md);
  backdrop-filter: blur(16px);
  min-width: 220px;
  padding: 8px;

  // Dark theme (default)
  background: linear-gradient(
    180deg,
    rgba(25, 30, 35, 0.98) 0%,
    rgba(18, 22, 26, 0.98) 100%
  );
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.4),
    0 2px 8px rgba(0, 0, 0, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.04);

  // Light theme
  .theme--light & {
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.98) 0%,
      rgba(248, 250, 252, 0.98) 100%
    );
    border: 1px solid rgba(0, 0, 0, 0.1);
    box-shadow:
      0 8px 24px rgba(0, 0, 0, 0.15),
      0 2px 8px rgba(0, 0, 0, 0.08),
      inset 0 1px 0 rgba(255, 255, 255, 0.8);
  }
}

// ============================================================================
// Connector Line
// ============================================================================

.slider-connector {
  position: absolute;
  top: -8px;
  left: 50%;
  transform: translateX(-50%);
  width: 1px;
  height: 8px;

  // Dark theme
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.1) 0%,
    rgba(255, 255, 255, 0.2) 100%
  );

  // Light theme
  .theme--light & {
    background: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0.08) 0%,
      rgba(0, 0, 0, 0.15) 100%
    );
  }
}

// ============================================================================
// Content
// ============================================================================

.slider-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

// ============================================================================
// Header
// ============================================================================

.slider-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.slider-label {
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-transform: uppercase;

  // Dark theme
  color: rgba(255, 255, 255, 0.5);

  // Light theme
  .theme--light & {
    color: rgba(0, 0, 0, 0.5);
  }
}

.slider-value {
  font-size: 14px;
  font-weight: 600;
  color: var(--v-primary-base);
  font-variant-numeric: tabular-nums;
}

// ============================================================================
// Track
// ============================================================================

.slider-track-container {
  padding: 8px 0;
}

.slider-track {
  position: relative;
  height: 6px;
  border-radius: 3px;
  cursor: pointer;
  overflow: visible;

  // Dark theme
  background: rgba(255, 255, 255, 0.08);

  // Light theme
  .theme--light & {
    background: rgba(0, 0, 0, 0.08);
  }
}

.slider-fill {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  background: linear-gradient(
    90deg,
    var(--v-primary-base) 0%,
    rgba(3, 98, 76, 0.7) 100%
  );
  border-radius: 3px;
  transition: width 0.05s ease-out;
}

// ============================================================================
// Tick Marks
// ============================================================================

.slider-ticks {
  position: absolute;
  top: -3px;
  left: 0;
  right: 0;
  height: 12px;
  pointer-events: none;
}

.slider-tick {
  position: absolute;
  width: 1px;
  height: 4px;
  transform: translateX(-50%);
  top: 8px;

  // Dark theme
  background: rgba(255, 255, 255, 0.15);

  // Light theme
  .theme--light & {
    background: rgba(0, 0, 0, 0.12);
  }

  &:first-child,
  &:last-child {
    height: 6px;
    top: 6px;
  }
}

// ============================================================================
// Thumb
// ============================================================================

.slider-thumb {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 20px;
  height: 20px;
  cursor: grab;
  z-index: 10;

  &:active {
    cursor: grabbing;
  }
}

.slider-thumb-inner {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 14px;
  height: 14px;
  background: linear-gradient(
    135deg,
    #ffffff 0%,
    #e0e0e0 100%
  );
  border-radius: 50%;
  box-shadow:
    0 2px 6px rgba(0, 0, 0, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  // Light theme - slightly darker thumb for contrast
  .theme--light & {
    background: linear-gradient(
      135deg,
      #ffffff 0%,
      #d0d0d0 100%
    );
    box-shadow:
      0 2px 6px rgba(0, 0, 0, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.9);
  }

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 4px;
    height: 4px;
    background: var(--v-primary-base);
    border-radius: 50%;
  }
}

.slider-thumb:hover .slider-thumb-inner {
  transform: translate(-50%, -50%) scale(1.1);
  box-shadow:
    0 3px 8px rgba(0, 0, 0, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);

  .theme--light & {
    box-shadow:
      0 3px 8px rgba(0, 0, 0, 0.25),
      inset 0 1px 0 rgba(255, 255, 255, 0.9);
  }
}

.slider-thumb:active .slider-thumb-inner {
  transform: translate(-50%, -50%) scale(1.05);
}

// ============================================================================
// Bounds Labels
// ============================================================================

.slider-bounds {
  display: flex;
  justify-content: space-between;
  font-size: 9px;
  font-weight: 500;
  letter-spacing: 0.3px;

  // Dark theme
  color: rgba(255, 255, 255, 0.3);

  // Light theme
  .theme--light & {
    color: rgba(0, 0, 0, 0.4);
  }
}

// ============================================================================
// Transitions
// ============================================================================

.slider-overlay-enter-active {
  animation: sliderIn 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}

.slider-overlay-leave-active {
  animation: sliderOut 0.12s ease-out;
}

@keyframes sliderIn {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(-8px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0) scale(1);
  }
}

@keyframes sliderOut {
  from {
    opacity: 1;
    transform: translateX(-50%) translateY(0) scale(1);
  }
  to {
    opacity: 0;
    transform: translateX(-50%) translateY(-4px) scale(0.98);
  }
}
</style>
